// Reads the cells of the first worksheet of an Excel 97-2003 (.xls) file, the
// format of the device list MagicInfo exports. No library: it unpacks the
// compound file container (CFB), takes the Workbook stream and walks its BIFF8
// records. Only text and number cells are read; formulas, formatting and the
// other sheets are ignored.

export class XlsReadError extends Error {}

const CFB_SIGNATURE = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];
// Sector ids from here up mark the end of a chain or an unused sector.
const MAX_REGULAR_SECTOR = 0xfffffffa;
const HEADER_DIFAT_ENTRIES = 109;
const DIR_ENTRY_SIZE = 128;
const STREAM_ENTRY = 2;
const ROOT_ENTRY = 5;

const BOF = 0x0809;
const EOF = 0x000a;
const FILEPASS = 0x002f;
const BOUNDSHEET = 0x0085;
const SST = 0x00fc;
const CONTINUE = 0x003c;
const LABELSST = 0x00fd;
const LABEL = 0x0204;
const NUMBER = 0x0203;
const RK = 0x027e;
const MULRK = 0x00bd;
const BIFF8 = 0x0600;
const GLOBALS_SUBSTREAM = 0x0005;
const WORKSHEET_SUBSTREAM = 0x0010;
const MAX_COLUMNS = 256;

interface IDirectoryEntry {
  name: string;
  type: number;
  start: number;
  size: number;
}

interface IBiffRecord {
  type: number;
  data: Uint8Array;
}

/** True when the bytes start like an Excel 97-2003 file (an OLE compound file). */
export function isXls(bytes: Uint8Array): boolean {
  return bytes.length >= 512 && CFB_SIGNATURE.every((byte, i) => bytes[i] === byte);
}

/** Rows of the first worksheet, every cell as text ('' for empty cells). Throws XlsReadError. */
export function readXlsRows(data: ArrayBuffer | Uint8Array): string[][] {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  return readFirstSheet(readCfbStream(bytes, ['workbook', 'book']));
}

function readCfbStream(file: Uint8Array, names: string[]): Uint8Array {
  if (!isXls(file)) throw new XlsReadError('Not an Excel 97-2003 file');
  const sectorShift = u16(file, 30);
  const miniShift = u16(file, 32);
  if ((sectorShift !== 9 && sectorShift !== 12) || miniShift !== 6) throw new XlsReadError('Unsupported sector size');
  const sectorSize = 1 << sectorShift;
  const miniSectorSize = 1 << miniShift;
  const sectorCount = Math.ceil((file.length - sectorSize) / sectorSize);

  const sector = (id: number): Uint8Array => {
    if (id >= sectorCount) throw new XlsReadError('Sector out of range');
    const start = (id + 1) * sectorSize;
    return file.subarray(start, start + sectorSize);
  };

  // The FAT sector ids: the first 109 are in the header, the rest in a chain of DIFAT sectors.
  const fatSectorTotal = u32(file, 44);
  if (fatSectorTotal > sectorCount) throw new XlsReadError('Invalid FAT size');
  const fatSectors: number[] = [];
  for (let i = 0; i < HEADER_DIFAT_ENTRIES && fatSectors.length < fatSectorTotal; i++) {
    fatSectors.push(u32(file, 76 + i * 4));
  }
  let difatId = u32(file, 68);
  for (let visited = 0; fatSectors.length < fatSectorTotal; visited++) {
    if (difatId >= MAX_REGULAR_SECTOR || visited >= sectorCount) throw new XlsReadError('Broken DIFAT chain');
    const difat = sector(difatId);
    for (let at = 0; at < sectorSize - 4 && fatSectors.length < fatSectorTotal; at += 4) fatSectors.push(u32(difat, at));
    difatId = u32(difat, sectorSize - 4);
  }
  const fat = fatSectors.flatMap((id) => u32List(sector(id)));

  const readChain = (start: number): Uint8Array =>
    concat(chain(start, fat, sectorCount).map(sector), sectorSize);

  const directory = readChain(u32(file, 48));
  const entries: IDirectoryEntry[] = [];
  for (let at = 0; at + DIR_ENTRY_SIZE <= directory.length; at += DIR_ENTRY_SIZE) {
    const nameBytes = Math.min(Math.max(u16(directory, at + 64) - 2, 0), 62);
    entries.push({
      name: text(directory, at, nameBytes >> 1, true).toLowerCase(),
      type: directory[at + 66],
      start: u32(directory, at + 116),
      size: u32(directory, at + 120),
    });
  }
  const root = entries[0];
  const entry = names.map((name) => entries.find((e) => e.type === STREAM_ENTRY && e.name === name)).find((e) => !!e);
  if (!root || root.type !== ROOT_ENTRY || !entry) throw new XlsReadError('No Workbook stream');

  let stream: Uint8Array;
  if (entry.size < u32(file, 56)) {
    // Small streams live in the mini stream, which is the root entry's data.
    const miniStream = readChain(root.start).subarray(0, root.size);
    const miniFat = u32List(readChain(u32(file, 60)));
    const miniSectors = chain(entry.start, miniFat, Math.ceil(miniStream.length / miniSectorSize)).map((id) => {
      if (id * miniSectorSize >= miniStream.length) throw new XlsReadError('Mini sector out of range');
      return miniStream.subarray(id * miniSectorSize, (id + 1) * miniSectorSize);
    });
    stream = concat(miniSectors, miniSectorSize);
  } else {
    stream = readChain(entry.start);
  }
  if (stream.length < entry.size) throw new XlsReadError('Workbook stream is truncated');
  return stream.subarray(0, entry.size);
}

/** Follows a sector chain; a chain longer than the file is broken (or loops). */
function chain(start: number, next: number[], limit: number): number[] {
  const ids: number[] = [];
  for (let id = start; id < MAX_REGULAR_SECTOR; id = next[id]) {
    if (ids.length >= limit || id >= next.length) throw new XlsReadError('Broken sector chain');
    ids.push(id);
  }
  return ids;
}

function readFirstSheet(stream: Uint8Array): string[][] {
  const globals = records(stream, 0);
  expectBof(globals.next().value, GLOBALS_SUBSTREAM);

  let sst: Uint8Array[] | undefined;
  let inSst = false;
  let sheetOffset = -1;
  for (const { type, data } of globals) {
    if (type === EOF) break;
    if (inSst && type === CONTINUE) {
      sst.push(data);
      continue;
    }
    inSst = type === SST;
    if (inSst) sst = [data];
    else if (type === FILEPASS) throw new XlsReadError('The workbook is password protected');
    // The sheet type byte is 0 for a worksheet (charts and macro sheets are skipped).
    else if (type === BOUNDSHEET && sheetOffset < 0 && data[5] === 0) sheetOffset = u32(data, 0);
  }
  if (sheetOffset < 0) throw new XlsReadError('No worksheet');
  const strings = sst ? readSst(sst) : [];

  const sheet = records(stream, sheetOffset);
  expectBof(sheet.next().value, WORKSHEET_SUBSTREAM);
  const rows: string[][] = [];
  const put = (row: number, column: number, value: string) => {
    if (column < MAX_COLUMNS) (rows[row] ??= [])[column] = value;
  };
  // An embedded chart brings its own BOF ... EOF; its records are not cells of this sheet.
  let depth = 1;
  for (const { type, data } of sheet) {
    if (type === BOF) depth++;
    if (type === EOF) depth--;
    if (depth === 0) break;
    if (depth > 1) continue;

    switch (type) {
      case LABELSST:
        put(u16(data, 0), u16(data, 2), strings[u32(data, 6)] ?? '');
        break;
      case LABEL:
        put(u16(data, 0), u16(data, 2), text(data, 9, u16(data, 6), (data[8] & 0x01) !== 0));
        break;
      case NUMBER:
        put(u16(data, 0), u16(data, 2), numberText(f64(data, 6)));
        break;
      case RK:
        put(u16(data, 0), u16(data, 2), numberText(rkValue(u32(data, 6))));
        break;
      case MULRK: {
        // Row, first column, then (format, RK value) pairs and the last column.
        const count = Math.floor((data.length - 6) / 6);
        for (let i = 0; i < count; i++) put(u16(data, 0), u16(data, 2) + i, numberText(rkValue(u32(data, 6 + i * 6))));
        break;
      }
    }
  }
  return Array.from(rows, (row) => Array.from(row ?? [], (cell) => cell ?? ''));
}

function* records(stream: Uint8Array, from: number): Generator<IBiffRecord> {
  for (let at = from; at + 4 <= stream.length; ) {
    const length = u16(stream, at + 2);
    if (at + 4 + length > stream.length) throw new XlsReadError('Truncated record');
    yield { type: u16(stream, at), data: stream.subarray(at + 4, at + 4 + length) };
    at += 4 + length;
  }
}

function expectBof(record: IBiffRecord | void, substream: number): void {
  if (!record || record.type !== BOF) throw new XlsReadError('Missing BOF record');
  if (u16(record.data, 0) !== BIFF8) throw new XlsReadError('Only Excel 97-2003 (BIFF8) workbooks are supported');
  if (u16(record.data, 2) !== substream) throw new XlsReadError('Unexpected substream');
}

/** The shared strings table (SST record plus the CONTINUE records after it). */
function readSst(segments: Uint8Array[]): string[] {
  const reader = new SegmentReader(segments);
  reader.skip(4);
  const count = reader.u32();
  const strings: string[] = [];
  for (let i = 0; i < count && !reader.atEnd(); i++) strings.push(reader.richString());
  return strings;
}

/** Reads across record boundaries, the way SST data runs over into CONTINUE records. */
class SegmentReader {
  private index = 0;
  private at = 0;

  constructor(private segments: Uint8Array[]) {}

  atEnd(): boolean {
    while (this.index < this.segments.length && this.at >= this.segments[this.index].length) {
      this.index++;
      this.at = 0;
    }
    return this.index >= this.segments.length;
  }

  byte(): number {
    if (this.atEnd()) throw new XlsReadError('Truncated shared strings');
    return this.segments[this.index][this.at++];
  }

  u16(): number {
    return this.byte() | (this.byte() << 8);
  }

  u32(): number {
    return (this.u16() | (this.u16() << 16)) >>> 0;
  }

  skip(count: number): void {
    while (count > 0) {
      if (this.atEnd()) throw new XlsReadError('Truncated shared strings');
      const step = Math.min(count, this.segments[this.index].length - this.at);
      this.at += step;
      count -= step;
    }
  }

  /** One string: length, option flags, optional run count and extended size, characters, then runs and extended data. */
  richString(): string {
    const length = this.u16();
    const flags = this.byte();
    const runs = flags & 0x08 ? this.u16() : 0;
    const extended = flags & 0x04 ? this.u32() : 0;
    let highByte = (flags & 0x01) !== 0;
    let value = '';
    for (let left = length; left > 0; ) {
      const segment = this.segments[this.index];
      if (!segment || this.at >= segment.length) {
        // Characters continued in the next record start with a fresh option flags byte.
        this.index++;
        this.at = 0;
        highByte = (this.byte() & 0x01) !== 0;
        continue;
      }
      const width = highByte ? 2 : 1;
      const take = Math.min(left, Math.floor((segment.length - this.at) / width));
      if (!take) throw new XlsReadError('Broken shared string');
      value += text(segment, this.at, take, highByte);
      this.at += take * width;
      left -= take;
    }
    this.skip(runs * 4 + extended);
    return value;
  }
}

/** Characters stored one byte each (high byte zero) or as UTF-16LE. */
function text(bytes: Uint8Array, at: number, count: number, highByte: boolean): string {
  const width = highByte ? 2 : 1;
  if (at + count * width > bytes.length) throw new XlsReadError('Truncated text');
  let value = '';
  for (let i = at; i < at + count * width; i += width) {
    value += String.fromCharCode(highByte ? bytes[i] | (bytes[i + 1] << 8) : bytes[i]);
  }
  return value;
}

/** An RK number: a 30-bit integer or the top 30 bits of a double, optionally times 100. */
function rkValue(rk: number): number {
  let value: number;
  if (rk & 0x02) {
    value = (rk | 0) >> 2;
  } else {
    const view = new DataView(new ArrayBuffer(8));
    view.setUint32(4, rk & 0xfffffffc, true);
    value = view.getFloat64(0, true);
  }
  return rk & 0x01 ? value / 100 : value;
}

function numberText(value: number): string {
  return Number.isFinite(value) ? String(value) : '';
}

/** Joins sectors; only the last one can be short (the end of the file or of the mini stream). */
function concat(parts: Uint8Array[], partSize: number): Uint8Array {
  const out = new Uint8Array(parts.length ? (parts.length - 1) * partSize + parts[parts.length - 1].length : 0);
  parts.forEach((part, i) => out.set(part, i * partSize));
  return out;
}

function u32List(bytes: Uint8Array): number[] {
  const values: number[] = [];
  for (let at = 0; at + 4 <= bytes.length; at += 4) values.push(u32(bytes, at));
  return values;
}

function u16(bytes: Uint8Array, at: number): number {
  if (at + 2 > bytes.length) throw new XlsReadError('Unexpected end of data');
  return bytes[at] | (bytes[at + 1] << 8);
}

function u32(bytes: Uint8Array, at: number): number {
  if (at + 4 > bytes.length) throw new XlsReadError('Unexpected end of data');
  return (bytes[at] | (bytes[at + 1] << 8) | (bytes[at + 2] << 16) | (bytes[at + 3] << 24)) >>> 0;
}

function f64(bytes: Uint8Array, at: number): number {
  if (at + 8 > bytes.length) throw new XlsReadError('Unexpected end of data');
  return new DataView(bytes.buffer, bytes.byteOffset + at, 8).getFloat64(0, true);
}
